<div class="property-selection-section">
    
    <!-- Search, Filter, and Sort Controls -->
    <div class="d-flex justify-content-between flex-wrap gap-3 mb-3">
        <div class="d-flex flex-column flex-md-row gap-3 flex-grow-1">
            <!-- Search Input -->
            <div class="input-group position-relative property-search-input">
                <input id="search" type="text" class="form-control form-control-solid ps-13 pe-5"
                    placeholder="Search by title" aria-label="Search properties"
                    [ngModel]="searchTerm"
                    (ngModelChange)="searchTerm = $event"
                    (keyup.enter)="onSearch()" aria-describedby="search">
                
                @if (searchTerm) {
                    <button
                        class="btn btn-link position-absolute top-50 translate-middle-y p-0 border-0 bg-transparent mx-2 input-group-clear-button"
                        type="button" 
                        (click)="clearSearch()" 
                        aria-label="Clear search">
                        <fa-duotone-icon icon="xmark" class="text-secondary fs-6" secondaryOpacity="0.9" size="lg" />
                    </button>
                }
                <div class="input-group-append">
                    <button class="btn btn-primary rounded-0 rounded-end"
                            type="button"
                            (click)="onSearch()"
                            id="button-addon2">
                        Search
                    </button>
                </div>
            </div>

            <div class="d-flex gap-3">
                <ms-property-search-filter
                    [localFilter]="propertiesFilter"
                    [hideStatusFilter]="true"
                    (filterApplied)="onFilterApplied($event)">
                </ms-property-search-filter>

                <!-- Sort Dropdown -->
                <ms-sort-dropdown 
                    [sortOptions]="sortOptions" 
                    [(selectedSortOption)]="orderBy"
                    (selectedSortOptionChange)="onSortChange($event)">
                </ms-sort-dropdown>
            </div>
        </div>
    </div>

    <!-- Property Count and Select All -->
    <div class="d-flex justify-content-between align-items-center mb-2">
        <div #jumpToElementMobile>
            <span class="me-2">Showing: {{ displayProperties.length }} of {{ totalCount | number }}</span>
            <span class="badge bg-primary">Selected: {{ effectiveSelectedCount | number }}</span>
        </div>
        <button 
            type="button" 
            class="btn btn-sm btn-ms-secondary d-md-none"
            (click)="toggleSelectAll()">
            <fa-duotone-icon icon="square-check" secondaryOpacity="0.2" size="lg" [hidden]="!isSelectAll" />
            <fa-duotone-icon icon="square" secondaryOpacity="0.2" size="lg" [hidden]="isSelectAll" />
            {{ isSelectAll ? 'Deselect All' : 'Select All' }}
        </button>
    </div>

    <!-- Property Table (Desktop) -->
    <div class="table-responsive d-none d-md-block">
        <table msResponsiveTable class="table table-hover placeholder-glow">
            <thead #jumpToElement>
                <tr>
                    <th msResponsiveTableColumn="checkbox" [priority]="1" scope="col">
                        <input
                            type="checkbox"
                            class="form-check-input fs-6"
                            [checked]="isSelectAll"
                            (change)="toggleSelectAll()" />
                    </th>
                    <th msResponsiveTableColumn="title" [priority]="1" scope="col" class="sortable" (click)="toggleSortDirection('Property Title')">
                        Property Title
                        <fa-duotone-icon icon="sort" class="ms-1" size="lg"
                            [primaryOpacity]="orderBy == propertyListSortOptions.TitleASC ? '0.9' : '0.3'"
                            [secondaryOpacity]="orderBy == propertyListSortOptions.TitleDESC ? '0.9' : '0.3'" />
                    </th>
                    <th msResponsiveTableColumn="address" [priority]="2" scope="col" class="sortable" (click)="toggleSortDirection('Address')">
                        Address Line 1
                        <fa-duotone-icon icon="sort" class="ms-1" size="lg"
                            [primaryOpacity]="orderBy == propertyListSortOptions.AddressASC ? '0.9' : '0.3'"
                            [secondaryOpacity]="orderBy == propertyListSortOptions.AddressDESC ? '0.9' : '0.3'" />
                    </th>
                    <th msResponsiveTableColumn="lotNumber" [priority]="3" scope="col" class="sortable" (click)="toggleSortDirection('Lot Number')">
                        Lot #
                        <fa-duotone-icon icon="sort" class="ms-1" size="lg"
                            [primaryOpacity]="orderBy == propertyListSortOptions.LotNumberASC ? '0.9' : '0.3'"
                            [secondaryOpacity]="orderBy == propertyListSortOptions.LotNumberDESC ? '0.9' : '0.3'" />
                    </th>
                    <th msResponsiveTableColumn="owners" [priority]="4" scope="col">Owner(s)</th>
                </tr>
            </thead>
            <tbody>
                @for (property of displayProperties; let i = $index; track property.propertyId) {
                    <tr class="align-middle" [className]="(i % 2 === 0) ? 'even' : 'odd'">
                        <td msResponsiveTableColumn="checkbox" [priority]="1">
                            <input
                                type="checkbox"
                                class="form-check-input"
                                [checked]="isSelected(property.propertyId)"
                                (change)="onPropertyToggle(property.propertyId, $event)" />
                        </td>
                        <td msResponsiveTableColumn="title" [priority]="1"><span [class.placeholder]="isLoadingProperties">{{ property.title | noValue }}</span></td>
                        <td msResponsiveTableColumn="address" [priority]="2"><span [class.placeholder]="isLoadingProperties">{{ property.addresses?.[0]?.primaryAddress | noValue }}</span></td>
                        <td msResponsiveTableColumn="lotNumber" [priority]="3"><span [class.placeholder]="isLoadingProperties">{{ property.lotNumber | noValue }}</span></td>
                        <td msResponsiveTableColumn="owners" [priority]="4" [title]="formatOwners(property)"><span [class.placeholder]="isLoadingProperties">{{ formatOwnersLimited(property) }}</span></td>
                    </tr>
                }
            </tbody>
            <tfoot ms-pagination [take]="take" (loadMoreInView)="loadMoreInView()" [(skip)]="skip"
                [hasNext]="hasNext" [itemCount]="displayProperties.length" (jumpToClicked)="jumpToTop($event)">
            </tfoot>
        </table>
    </div>

    <!-- Property Cards (Mobile) -->
    <div class="d-block d-md-none">
        @for (property of displayProperties; track property.propertyId) {
            <div class="card mb-3">
                <div class="card-body">
                    <div class="d-flex align-items-start">
                        <div class="d-flex align-items-center me-3">
                            <ms-mobile-card-checkbox
                                [item]="property" [selectionPrimaryKey]="SelectionPrimaryKeys.PropertyId"
                                [isSelected]="isSelected(property.propertyId)"
                                (clicked)="onToggle($event)"
                                [checkboxSize]="'50px'"
                                [checkboxShape]="'circle'" />
                        </div>
                        <div class="flex-grow-1">
                            <h6 class="card-title mb-2" [class.placeholder]="isLoadingProperties">{{ property.title | noValue }}</h6>
                            <p class="card-text mb-1"><strong>Address:</strong> <span [class.placeholder]="isLoadingProperties">{{ property.addresses?.[0]?.primaryAddress | noValue }}</span></p>
                            <p class="card-text mb-1"><strong>Lot #:</strong> <span [class.placeholder]="isLoadingProperties">{{ property.lotNumber | noValue }}</span></p>
                            <p class="card-text mb-0"><strong>Owner(s):</strong> <span [class.placeholder]="isLoadingProperties">{{ formatOwners(property) }}</span></p>
                        </div>
                    </div>
                </div>
            </div>
        }
        <div ms-in-view-port (inViewPort)="loadMoreInView()" ms-pagination [take]="take" [(skip)]="skip"
            [hasNext]="hasNext" [itemCount]="displayProperties.length" (jumpToClicked)="jumpToTop($event)">
        </div>
    </div>
</div>
